import React from "react";
import { AxiosResponse } from "axios";
import DataGrid, {
    Pager,
    Paging,
    FilterRow,
    Column,
    Sorting,
    Editing,
    Export,
    SearchPanel,
    StateStoring,
} from "devextreme-react/data-grid";
import { LoadIndicator } from "devextreme-react";
import promotionTemplatesService, { promotionTemplatesGridRowItem } from "../../services/PromotionTemplatesService";
import lookupService, { LookupTypeItem } from "../../services/LookupService";
import gridUtils from "../grid/GridUtilities";
import sharedUtils from "../grid/sharedUtilities";
import { onExportingEvent } from "../../types/DevExtremeTypes";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver";
import MaskedStatusForActivePromotionTemplate from "./MaskedStatusForActivePromotionTemplate";
import ServiceFeeCellComponent from "../../../src/components/ServiceFeeMatrix/ServiceFeeCellComponent"
import PromotionTemplateCellComponent from "./PromotionTemplateCellComponent"
//props
interface PromotionTemplatesGridProps {
    refreshSignal: boolean;
}

// State
interface PromotionTemplatesGridState {
    promotionTemplatesDataSource: promotionTemplatesGridRowItem[];
    showProgressIndicator: boolean;
    clientLookUp: LookupTypeItem[];
    serviceLookUp: LookupTypeItem[];
    subServiceLookUp: LookupTypeItem[];
    serviceFeeLookUp: LookupTypeItem[];
}

// Component - displays the Rate Matrix Grid
class PromotionTemplatesGrid extends React.Component<PromotionTemplatesGridProps> {
    //Initialize the component's state.
    state: PromotionTemplatesGridState;
    promotionTemplatesService: promotionTemplatesService;
    dropdownService: lookupService;
    gridUtils: gridUtils;
    sharedUtils: sharedUtils;
    constructor(props: PromotionTemplatesGridProps) {
        super(props);
        this.promotionTemplatesService = new promotionTemplatesService();
        this.dropdownService = new lookupService();
        this.sharedUtils = new sharedUtils();
        // Initialize state and services/utils
        this.state = {
            promotionTemplatesDataSource: [],
            showProgressIndicator: false,
            clientLookUp: [],
            serviceLookUp: [],
            subServiceLookUp: [],
            serviceFeeLookUp: [],
        };
        this.gridUtils = new gridUtils();
    }

    componentDidMount() {
        this.fetchServiceFeeDataSource();
    }

    //When a component is updated this lifecycle method is called, and a change in props here would trigger this.
    componentDidUpdate(prevProps: PromotionTemplatesGridProps) {
        if (this.props.refreshSignal !== prevProps.refreshSignal) {
            // refresh the grid
            this.fetchServiceFeeDataSource();
        }
    }

    fetchServiceFeeDataSource = () => {
        this.setState({
            showProgressIndicator: true,
        });
        this.promotionTemplatesService.getPromotionTemplateGridData().then(this.handleSuccess).catch(this.handleError);
    };

    handleSuccess = (response: AxiosResponse<any>) => {

        this.setState({
            promotionTemplatesDataSource: response.data.data,
            showProgressIndicator: false,
        });
    };

    handleError = (error: AxiosResponse<any>) => {
        this.setState({
            showProgressIndicator: false,
        });
    };

    onRowUpdatedInserted = () => {
        this.fetchServiceFeeDataSource();
    };

    onExporting = (e: onExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");

        exportDataGrid({
            component: e.component,
            worksheet: worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: "application/octet-stream" }), "DataGrid.xlsx");
            });
        });
        e.cancel = true;
    };
    OriginatorMultipliersCell = ({ data }: any) => {
        if (!data.value || data.value.length === 0) {
            return <span>-</span>;
        }

        return (
            <div>
                {data.value.map((item: { value: string; multiplier: string }, index: number) => (
                    <div key={index}>{`${item.value} - ${item.multiplier}`}</div>
                ))}
            </div>
        );
    };
    render() {
        return (
            <div>
                {this.state.showProgressIndicator ? (
                    <div className="starter-template">
                        <LoadIndicator
                            id="simple-grid-indicator"
                            height={100}
                            width={100}
                            visible={this.state.showProgressIndicator}
                        />
                    </div>
                ) : (
                    <DataGrid
                        dataSource={this.state.promotionTemplatesDataSource}
                        showBorders={false}
                        showColumnLines={false}
                        hoverStateEnabled={true}
                        allowColumnReordering={true}
                        allowColumnResizing={true}
                        columnResizingMode={"widget"}
                        onExporting={this.onExporting}
                        columnAutoWidth={false}
                    >
                        <SearchPanel visible={true} placeholder={"Search"} />
                        <Sorting mode="single" />
                        <Export enabled={true} allowExportSelectedData={true} />
                        <Paging defaultPageSize={10} />
                        <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
                        <StateStoring
                            enabled={true}
                            type="localStorage"
                            storageKey="promotion_templates_grid"
                            savingTimeout={0}
                        />

                        <Column dataField="id" caption="ID" allowSorting={true} />

                        <Column dataField="businessEntity" caption="CLIENT" allowSorting={true} />

                        <Column dataField="brandId" caption="BRAND ID" allowSorting={true} />

                        <Column dataField="brandName" caption="BRAND NAME" allowSorting={true} />

                        <Column dataField="serviceType" caption="SERVICE TYPE" allowSorting={true} />

                        <Column dataField="serviceSubType" caption="SERVICE SUB TYPE" allowSorting={true} />

                        <Column dataField="typeLookUp" caption="TYPE" allowSorting={true} />

                        <Column dataField="linkType" caption="LINK TYPE" allowSorting={true} />

                        <Column dataField="link" caption="TEMPLATE LINK" allowSorting={false} width={100}/>

                        <Column dataField="promotionTemplateTextStyle" caption="TEXT STYLE" allowSorting={true} />

                        <Column dataField="description" caption="DESCRIPTION" allowSorting={true} />
                        <Column
                            dataField="active"
                            caption="ACTIVE"
                            allowSorting={true}
                            dataType="string"
                            //cellComponent={MaskedStatusForActivePromotionTemplate}
                        />
                        <Column cellComponent={PromotionTemplateCellComponent} width={50}></Column>
                        <Editing mode="row" useIcons={true} allowDeleting={false} />

                        <FilterRow visible={true} applyFilter="auto" />
                    </DataGrid>
                )}
            </div>
        );
    }
}

export default PromotionTemplatesGrid;
